import { computed, onMounted, onUnmounted, ref, toValue, watchEffect } from 'vue'

import { storeToRefs } from 'pinia'

import { useGlobal } from '@common/core-layer/composables/useGlobal'
import { useSockets } from '@common/core-layer/composables/useSockets'
import { useLocation } from '@common/location-layer/composables/useLocation'

import { useGroupsUIStore } from '@communities/groups-layer/stores/groups.store'

import { useChatsQueryStore } from '../stores/chats.query-store'
import { useChatsUIStore } from '../stores/chats.ui-store'

import type { ChatSocketMessage } from '../types/socket.types'

const listenerCount = ref(0)
const currentLocationId = ref<string | null>(null)
let messageHandler: ((message: ChatSocketMessage) => void) | null = null
let reconnectHandler: (() => void) | null = null

export const useChatsRealtime = () => {
  const queryStore = useChatsQueryStore()
  const uiStore = useChatsUIStore()
  const { selectedConversationId, activeChatWindows } = storeToRefs(uiStore)
  const groupsStore = useGroupsUIStore()
  const { locationId: clientPortalLocationId } = useLocation()
  const { isGokollab } = useGlobal()
  const { on, off, isConnected } = useSockets()

  const resolvedLocationId = computed(() => {
    if (toValue(isGokollab)) {
      return toValue(clientPortalLocationId) || null
    }

    return groupsStore.currentGroup?.locationId || toValue(clientPortalLocationId) || null
  })

  watchEffect(() => {
    currentLocationId.value = resolvedLocationId.value
  })

  const handleReconnect = () => {
    if (!currentLocationId.value) {
      return
    }

    const conversationIds = new Set<string>()
    if (selectedConversationId.value) {
      conversationIds.add(selectedConversationId.value)
    }

    activeChatWindows.value.forEach(window => {
      conversationIds.add(window.conversationId)
    })

    queryStore.handleSocketReconnect(currentLocationId.value, Array.from(conversationIds))
  }

  const registerListeners = () => {
    if (listenerCount.value > 0) {
      listenerCount.value += 1
      return
    }

    messageHandler = message => {
      const payloadLocationId = (message.payload as { locationId?: string } | null)?.locationId
      const overrideLocationId = message.locationId || payloadLocationId ? undefined : currentLocationId.value || undefined

      queryStore.handleSocketMessage(message, overrideLocationId)
    }

    reconnectHandler = () => {
      handleReconnect()
    }

    on('message', messageHandler)
    on('connect', reconnectHandler)
    on('connectionState', reconnectHandler)
    listenerCount.value = 1

    if (isConnected.value) {
      handleReconnect()
    }
  }

  const unregisterListeners = () => {
    if (listenerCount.value === 0) {
      return
    }

    listenerCount.value -= 1
    if (listenerCount.value > 0) {
      return
    }

    if (messageHandler) {
      off('message', messageHandler)
      messageHandler = null
    }

    if (reconnectHandler) {
      off('connect', reconnectHandler)
      off('connectionState', reconnectHandler)
      reconnectHandler = null
    }
  }

  onMounted(registerListeners)
  onUnmounted(unregisterListeners)
}
